import querystring from 'query-string';
import {
    RangeValueFilterStateKeys,
    type FilterStates,
    type FilterStatesKey,
} from '@modules-rocco/filters/types/filter-states';
import type { SearchSortOptions } from '@modules-rocco/search/types/enum/search-sort-options';
import { convertFilterStateToURLParams } from '@modules-rocco/filters/data-mappers/filter-states-to-url';
import { AWARDS_ALL_SENTINEL } from '@modules-rocco/filters/filter-state/awards-filter-state';
import { IN_PAGE_SEARCH_PARAM_KEY } from '~/modules/professional/config/constants';
import { FAMILY_BRANDS_PARAM } from '@modules/professional/util/family-brand-filter';

const QUERY_FORMAT = { arrayFormat: 'comma' } as const;

/**
 * Professional tab URLs carry the in-page sections anchor so the page opens
 * scrolled to the list area, matching the URLs the tabs have always produced.
 */
const PROFESSIONAL_SECTIONS_ANCHOR = 'sections';

/**
 * Params owned by the page rather than the filter states; they are carried
 * over from the current URL so applying a filter never drops them.
 */
const PRESERVED_PARAM_KEYS = [
    IN_PAGE_SEARCH_PARAM_KEY,
    'categoryLink',
    // The Products tab's brand-family scope. It is not a filter state, so a
    // sort or filter change would otherwise drop it and narrow the grid back to
    // the profile's own products.
    FAMILY_BRANDS_PARAM,
];

interface ProfessionalTabURLOptions {
    ignoreFilterKeys?: FilterStatesKey[];
    defaultSort?: SearchSortOptions;
    defaultViewProjectsBy?: string;
}

/**
 * Create a professional list-tab URL from Rocco filter states.
 *
 * Unlike `mapFilterStatesToURL` (directory browse), tab URLs stay on the
 * current pathname with plain query params: no `browse` param, no path-encoded
 * sort segment, and pagination resets on every filter change.
 */
export const mapFilterStatesToProfessionalTabURL = (
    filterStates: FilterStates,
    originalURL: string,
    options: ProfessionalTabURLOptions = {},
): string => {
    const {
        ignoreFilterKeys = [],
        defaultSort,
        defaultViewProjectsBy,
    } = options;

    const params = convertFilterStateToURLParams(
        filterStates,
        ignoreFilterKeys,
        defaultSort,
    );

    // Single-choice states serialise as a plain string at runtime even though
    // the param type is declared as an array.
    const viewProjectsBy = Array.isArray(params.viewProjectsBy)
        ? params.viewProjectsBy.join(',')
        : params.viewProjectsBy;
    if (defaultViewProjectsBy && viewProjectsBy === defaultViewProjectsBy) {
        delete params.viewProjectsBy;
    }

    // Ranges convert to a "min,max" string; split back to an array so the
    // comma survives stringification raw instead of percent-encoded.
    for (const key of RangeValueFilterStateKeys) {
        const value = params[key];
        if (typeof value === 'string' && value.includes(',')) {
            params[key] = value.split(',');
        }
    }

    // A selection containing the sentinel means "everything" — collapse to
    // the legacy URL shape instead of enumerating every award.
    if (
        Array.isArray(params.awards) &&
        params.awards.includes(AWARDS_ALL_SENTINEL)
    ) {
        params.awards = [AWARDS_ALL_SENTINEL];
    }

    const original = new URL(originalURL, 'http://dummy');
    for (const key of PRESERVED_PARAM_KEYS) {
        const value = original.searchParams.get(key);
        if (value) {
            params[key] = value;
        }
    }

    const query = querystring
        .stringify(params, QUERY_FORMAT)
        .replace(/%20/g, '+');
    const path = query ? `${original.pathname}?${query}` : original.pathname;

    return `${path}#${PROFESSIONAL_SECTIONS_ANCHOR}`;
};
